import React, { useState } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Lock,
  Unlock,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from "lucide-react";
import { Modal } from "../common/Modal";
import { Opportunity, PlanReadinessReport, UserProfile, PlanReadinessCheck } from "../../types";
import { PlanSubTab } from "./RequirementsTab";
import {
  freezeOpportunityPlan,
  unfreezeOpportunityPlan,
} from "../../services/planReadinessService";

interface PlanReadinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  report: PlanReadinessReport;
  user?: UserProfile | null;
  onNavigateToSubTab: (tab: PlanSubTab) => void;
  onFreezeSuccess?: () => void;
}

export const PlanReadinessModal: React.FC<PlanReadinessModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  report,
  user,
  onNavigateToSubTab,
  onFreezeSuccess,
}) => {
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnfreezeConfirm, setShowUnfreezeConfirm] = useState(false);
  const [unfreezeReason, setUnfreezeReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentUser = {
    displayName: user?.displayName || "Mark L",
    email: user?.email || "mark@mlconnections.com",
  };

  const isFrozen = Boolean(opportunity.planFrozen);

  const toggleExpand = (id: string) => {
    setExpandedCheckId((prev) => (prev === id ? null : id));
  };

  const handleFreeze = async () => {
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      await freezeOpportunityPlan(
        opportunity.id,
        currentUser,
        opportunity.stageHistory || [],
        report.overallScore,
      );
      if (onFreezeSuccess) onFreezeSuccess();
      onClose();
    } catch (err) {
      console.error("Error freezing plan:", err);
      setErrorMsg("Failed to freeze plan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnfreeze = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unfreezeReason.trim()) return;

    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      await unfreezeOpportunityPlan(
        opportunity.id,
        currentUser,
        opportunity.stageHistory || [],
        unfreezeReason.trim(),
      );
      setShowUnfreezeConfirm(false);
      setUnfreezeReason("");
      if (onFreezeSuccess) onFreezeSuccess();
      onClose();
    } catch (err) {
      console.error("Error unfreezing plan:", err);
      setErrorMsg("Failed to unfreeze plan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: PlanReadinessCheck["status"]) => {
    switch (status) {
      case "pass":
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "blocker":
        return <XCircle className="h-5 w-5 text-rose-500" />;
    }
  };

  const getStatusBadge = (status: PlanReadinessCheck["status"]) => {
    switch (status) {
      case "pass":
        return (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
            Pass
          </span>
        );
      case "warning":
        return (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
            Warning
          </span>
        );
      case "blocker":
        return (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
            Blocker
          </span>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Plan Readiness Gate & Baseline Freeze"
      maxWidth="2xl"
    >
      <div className="space-y-5">
        {/* Frozen Banner if locked */}
        {isFrozen && (
          <div className="flex items-center justify-between rounded-xl border border-emerald-300 bg-emerald-50/80 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-sm">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-emerald-950 dark:text-emerald-200">
                  Plan Baseline Frozen
                </h4>
                <p className="text-xs text-emerald-800 dark:text-emerald-300">
                  Frozen on{" "}
                  {opportunity.planFrozenAt
                    ? new Date(opportunity.planFrozenAt).toLocaleDateString()
                    : "record"}{" "}
                  by {opportunity.planFrozenBy || "Proposal Lead"}. Opportunity is in{" "}
                  <strong>{opportunity.stage}</strong> stage.
                </p>
              </div>
            </div>
            {!showUnfreezeConfirm && (
              <button
                type="button"
                onClick={() => setShowUnfreezeConfirm(true)}
                className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <Unlock className="h-3.5 w-3.5" />
                <span>Unfreeze Plan</span>
              </button>
            )}
          </div>
        )}

        {/* Unfreeze Reason Form */}
        {showUnfreezeConfirm && (
          <form
            onSubmit={handleUnfreeze}
            className="rounded-xl border border-amber-300 bg-amber-50/70 p-4 space-y-3 dark:border-amber-800 dark:bg-amber-950/30"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-amber-900 dark:text-amber-200">
                Reason for Reopening Plan Baseline:
              </span>
              <button
                type="button"
                onClick={() => setShowUnfreezeConfirm(false)}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
            <input
              type="text"
              required
              value={unfreezeReason}
              onChange={(e) => setUnfreezeReason(e.target.value)}
              placeholder="e.g. Client issued major Addendum #2 shifting technical scope"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowUnfreezeConfirm(false)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:text-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !unfreezeReason.trim()}
                className="btn-animate rounded-lg bg-amber-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
              >
                {isSubmitting ? "Unfreezing..." : "Confirm & Unlock Plan"}
              </button>
            </div>
          </form>
        )}

        {/* Readiness Score Card */}
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#2ba5b5]" />
              <span className="text-sm font-bold text-slate-900 dark:text-white">
                Readiness Score: {report.overallScore}%
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {report.overallScore >= 90
                ? "Excellent: Plan is complete and fully structured for Drafting."
                : report.overallScore >= 70
                  ? "Good: Minor items need verification before drafting."
                  : "Attention Needed: Key requirements or section leads remain unassigned."}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> {report.passedCount} Passed
            </span>
            {report.warningsCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" /> {report.warningsCount} Warnings
              </span>
            )}
            {report.blockersCount > 0 && (
              <span className="flex items-center gap-1 text-rose-600 dark:text-rose-400">
                <XCircle className="h-3.5 w-3.5" /> {report.blockersCount} Blockers
              </span>
            )}
          </div>
        </div>

        {/* Audit Checks List */}
        <div className="space-y-2">
          {report.checks.map((check) => {
            const isExpanded = expandedCheckId === check.id;
            return (
              <div
                key={check.id}
                className="rounded-lg border border-slate-200 bg-white transition-all dark:border-slate-800 dark:bg-slate-900"
              >
                <div
                  className="flex items-center justify-between p-3 cursor-pointer select-none"
                  onClick={() => toggleExpand(check.id)}
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(check.status)}
                    <div>
                      <div className="text-xs font-bold text-slate-900 dark:text-white">
                        {check.label}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        {check.summary}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    {getStatusBadge(check.status)}
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      aria-label="Toggle details"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/70 p-3 space-y-2 dark:border-slate-800 dark:bg-slate-900/40">
                    {check.details && check.details.length > 0 && (
                      <ul className="list-disc pl-5 text-[11px] text-slate-600 space-y-0.5 dark:text-slate-300">
                        {check.details.map((detail, idx) => (
                          <li key={idx}>{detail}</li>
                        ))}
                      </ul>
                    )}
                    {check.actionSubTab && (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (check.actionSubTab) {
                              onNavigateToSubTab(check.actionSubTab);
                              onClose();
                            }
                          }}
                          className="btn-animate inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ba5b5] hover:underline"
                        >
                          <span>{check.actionLabel || "View Tab"}</span>
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {errorMsg && (
          <div className="rounded-lg bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {errorMsg}
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex items-center justify-between border-t border-slate-200 pt-4 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Close
          </button>

          {!isFrozen ? (
            <button
              type="button"
              disabled={isSubmitting || report.blockersCount > 0}
              onClick={handleFreeze}
              className={`btn-animate flex items-center gap-2 rounded-lg px-5 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
                report.blockersCount > 0
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-[#2ba5b5] hover:bg-[#238a97]"
              }`}
              title={
                report.blockersCount > 0
                  ? "Resolve blocker items before freezing baseline"
                  : "Freeze response plan baseline and advance to Drafting"
              }
            >
              <Lock className="h-4 w-4" />
              <span>{isSubmitting ? "Freezing..." : "Freeze Plan & Advance to Drafting"}</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-600 font-semibold dark:text-emerald-400">
                Plan Baseline Active
              </span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
